class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.onDiscountClick.bind(this));
    this.addEventListener('keydown', this.onDiscountInputKeydown.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.#restoreDiscountPillsOnLoad();
  }

  onDiscountClick(event) {
    if (event.target.closest('.cart-totals__remove-discount')) {
      event.preventDefault();
      this.removeDiscountCode();
      return;
    }

    const button = event.target.closest('.cart-totals__apply-btn');
    if (!button) return;

    event.preventDefault();
    this.applyDiscountCode();
  }

  onDiscountInputKeydown(event) {
    if (event.key !== 'Enter') return;
    if (!event.target.closest('.cart-totals__code-input')) return;

    event.preventDefault();
    this.applyDiscountCode();
  }

  applyDiscountCode() {
    const input = this.querySelector('.cart-totals__code-input');
    const applyButton = this.querySelector('.cart-totals__apply-btn');
    const errors = document.getElementById('CartDrawer-CartErrors');

    if (!input) return;
    if (applyButton?.classList.contains('loading')) return;

    const code = input.value.trim();
    if (!code) {
      if (errors) errors.textContent = window.cartStrings.discountEmpty;
      input.focus();
      return;
    }

    if (errors) errors.textContent = '';
    this.setDiscountLoading(true);

    const body = JSON.stringify({
      discount: code,
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text().then((text) => ({ ok: response.ok, text })))
      .then(({ ok, text }) => {
        const parsedState = JSON.parse(text);

        if (!ok || parsedState.status) {
          throw new Error(parsedState.description || parsedState.message || window.cartStrings.error);
        }

        if (!this.#discountWasApplied(parsedState, code)) {
          throw new Error(window.cartStrings.discountError);
        }

        return this.#refreshCartDrawerAfterDiscount(parsedState, code);
      })
      .catch((error) => {
        if (errors) errors.textContent = error.message || window.cartStrings.error;
        this.setDiscountLoading(false);
      });
  }

  removeDiscountCode() {
    const errors = document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = '';
    this.setDiscountLoading(true);

    const body = JSON.stringify({
      discount: '',
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text().then((text) => ({ ok: response.ok, text })))
      .then(({ ok, text }) => {
        const parsedState = JSON.parse(text);

        if (!ok || parsedState.status) {
          throw new Error(parsedState.description || parsedState.message || window.cartStrings.error);
        }

        return this.#refreshCartDrawerAfterDiscount(parsedState);
      })
      .catch((error) => {
        if (errors) errors.textContent = error.message || window.cartStrings.error;
        this.setDiscountLoading(false);
      });
  }

  #discountWasApplied(parsedState, code) {
    const normalizedCode = code.toLowerCase();
    const discountEntry = parsedState.discount_codes?.find(
      (discount) => discount.code.toLowerCase() === normalizedCode
    );

    if (discountEntry?.applicable) return true;
    if ((parsedState.cart_level_discount_applications?.length ?? 0) > 0) return true;
    if ((parsedState.total_discount ?? 0) > 0) return true;

    return false;
  }

  #refreshCartDrawerAfterDiscount(parsedState, appliedCode = '') {
    const sectionHtml = parsedState.sections?.['cart-drawer'];

    const finishRefresh = () => {
      this.setDiscountLoading(false);
      this.#syncDiscountUiAfterRefresh(appliedCode, parsedState);
    };

    if (sectionHtml) {
      this.refreshCartDrawerFromSection(sectionHtml);
      this.updateCartIconBubble(parsedState.sections['cart-icon-bubble']);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-discount', cartData: parsedState });
      finishRefresh();
      return Promise.resolve();
    }

    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        this.refreshCartDrawerFromSection(responseText);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-discount', cartData: parsedState });
        finishRefresh();
      });
  }

  #syncDiscountUiAfterRefresh(appliedCode, parsedState) {
    const details = this.querySelector('#Details-DiscountCode');
    const input = this.querySelector('.cart-totals__code-input');
    const errors = document.getElementById('CartDrawer-CartErrors');

    if (errors) errors.textContent = '';

    if (input) {
      input.value = '';
      input.disabled = false;
      input.removeAttribute('aria-busy');
    }

    if (details) {
      details.removeAttribute('open');
    }

    this.#syncDiscountPillsFromCart(parsedState);

    if (!appliedCode) return;

    const hasAppliedPill = Boolean(
      this.querySelector(`.cart-totals__applied-pill[data-discount-code="${CSS.escape(appliedCode)}"]`)
    );

    if (hasAppliedPill) return;

    this.#renderAppliedDiscountPill(appliedCode);
    details?.classList.add('cart-totals__discount-details--has-code');
  }

  #restoreDiscountPillsOnLoad() {
    const discountRow = this.querySelector('.cart-totals__discount-row');
    const existingPill = this.querySelector('.cart-totals__applied-pill');

    if (existingPill) {
      this.querySelector('#Details-DiscountCode')?.classList.add('cart-totals__discount-details--has-code');
      return;
    }

    if (!discountRow) return;

    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => this.#syncDiscountPillsFromCart(cart))
      .catch(() => {});
  }

  #getDiscountLabelsFromCart(cart) {
    const labels = [];
    const seen = new Set();

    for (const entry of cart.discount_codes ?? []) {
      const code = entry?.code?.trim();
      if (!code) continue;

      const key = code.toLowerCase();
      if (seen.has(key)) continue;

      seen.add(key);
      labels.push(code);
    }

    for (const application of cart.cart_level_discount_applications ?? []) {
      const title = application?.title?.trim();
      if (!title) continue;

      const key = title.toLowerCase();
      if (seen.has(key)) continue;

      seen.add(key);
      labels.push(title);
    }

    return labels;
  }

  #syncDiscountPillsFromCart(cart) {
    const details = this.querySelector('#Details-DiscountCode');
    const labels = this.#getDiscountLabelsFromCart(cart);

    if (!labels.length) {
      if (!this.querySelector('.cart-totals__applied-pill')) {
        details?.classList.remove('cart-totals__discount-details--has-code');
      }
      return;
    }

    for (const label of labels) {
      this.#renderAppliedDiscountPill(label);
    }

    details?.classList.add('cart-totals__discount-details--has-code');
  }

  #renderAppliedDiscountPill(code) {
    if (!code) return;

    let container = this.querySelector('.cart-totals__applied-codes');

    if (!container) {
      container = document.createElement('div');
      container.className = 'cart-totals__applied-codes';

      const row = document.createElement('div');
      row.className = 'cart-totals__applied-row';
      container.appendChild(row);

      const details = this.querySelector('#Details-DiscountCode');
      details?.insertAdjacentElement('afterend', container);
    }

    const row = container.querySelector('.cart-totals__applied-row');
    if (!row || row.querySelector(`[data-discount-code="${CSS.escape(code)}"]`)) return;

    const pill = document.createElement('div');
    pill.className = 'cart-totals__applied-pill';
    pill.dataset.discountCode = code;

    const label = document.createElement('span');
    label.className = 'cart-totals__applied-code';
    label.textContent = code;

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'cart-totals__remove-discount';
    removeButton.setAttribute('aria-label', `Remove discount code ${code}`);

    const iconWrapper = document.createElement('span');
    iconWrapper.className = 'svg-wrapper';
    iconWrapper.setAttribute('aria-hidden', 'true');
    iconWrapper.innerHTML = this.#getDiscountRemoveIconMarkup();

    removeButton.appendChild(iconWrapper);
    pill.append(label, removeButton);
    row.appendChild(pill);
  }

  #getDiscountRemoveIconMarkup() {
    const templateIcon = this.querySelector('.cart-totals__remove-discount .svg-wrapper');

    if (templateIcon) return templateIcon.innerHTML;

    return '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 12 13" aria-hidden="true"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="M8.486 9.33 2.828 3.67M2.885 9.385l5.544-5.77"/></svg>';
  }

  setDiscountLoading(isLoading) {
    const codeRow = this.querySelector('.cart-totals__code-row');
    const applyButton = this.querySelector('.cart-totals__apply-btn');
    const input = this.querySelector('.cart-totals__code-input');
    const status = document.getElementById('CartDrawer-DiscountStatus');
    const label = applyButton?.querySelector('.cart-totals__apply-btn-label');
    const spinner = applyButton?.querySelector('.loading__spinner');

    if (!applyButton) return;

    if (isLoading) {
      codeRow?.classList.add('cart-totals__code-row--loading');
      applyButton.classList.add('loading');
      applyButton.disabled = true;
      applyButton.setAttribute('aria-busy', 'true');
      spinner?.classList.remove('hidden');
      if (input) {
        input.disabled = true;
        input.setAttribute('aria-busy', 'true');
      }
      if (status) {
        status.textContent = window.cartStrings.discountApplying || '';
        status.classList.remove('visually-hidden');
      }
      return;
    }

    codeRow?.classList.remove('cart-totals__code-row--loading');
    applyButton.classList.remove('loading');
    applyButton.disabled = false;
    applyButton.removeAttribute('aria-busy');
    spinner?.classList.add('hidden');
    if (input) {
      input.disabled = false;
      input.removeAttribute('aria-busy');
    }
    if (status) {
      status.textContent = '';
      status.classList.add('visually-hidden');
    }
  }

  refreshCartDrawerFromSection(sectionHtml) {
    if (!sectionHtml) return;

    const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const sectionRoot = html.querySelector('.shopify-section') || html;
    const selectors = ['cart-drawer-items', '.cart-drawer__footer'];

    for (const selector of selectors) {
      const targetElement = document.querySelector(selector);
      const sourceElement = sectionRoot.querySelector(selector);

      if (targetElement && sourceElement) {
        targetElement.replaceWith(sourceElement);
      }
    }
  }

  updateCartIconBubble(sectionHtml) {
    if (!sectionHtml) return;

    const cartIcon = document.getElementById('cart-icon-bubble');
    if (!cartIcon) return;

    const sectionInnerHtml = this.getSectionInnerHTML(sectionHtml, '.shopify-section');
    if (sectionInnerHtml) cartIcon.innerHTML = sectionInnerHtml;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('animate', 'active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
